import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Paper,
} from '@mui/material';
import { Fade } from '@mui/material';
import { QueryStats } from '@mui/icons-material';
import Info from './Info';
import { useAdminLanguage } from './AdminI18n';

const StatisticsAdmin = () => {
  const { t } = useAdminLanguage();
  const [statisticsEnabled, setStatisticsEnabled] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch('/api/settings');
        if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
        const data = await response.json();
        setStatisticsEnabled(data.statisticsEnabled ?? true);
      } catch (err) {
        console.error('Erreur lors de la récupération des paramètres:', err);
        setError('Une erreur est survenue lors du chargement des paramètres.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSwitchChange = async (event) => {
    const newValue = event.target.checked;
    setStatisticsEnabled(newValue);
    setSubmitting(true);
    setMessage('');
    setError(null);

    try {
      const token = sessionStorage.getItem('authToken');
      const response = await fetch('/api/settings', {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ statisticsEnabled: newValue }),
      });

      if (response.ok) {
        setMessage(newValue 
          ? t('Statistiques activées avec succès !') 
          : t('Statistiques désactivées avec succès !')
        );
      } else {
        const data = await response.json();
        setError(data.error || t('Erreur lors de la mise à jour.'));
        // Revenir à l'état précédent en cas d'erreur
        setStatisticsEnabled(!newValue);
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError(t('Erreur lors de la communication avec le serveur.'));
      setStatisticsEnabled(!newValue);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in={!loading} timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des Statistiques')}
          </Typography>

          <Info type="statistics" />

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <QueryStats sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {t('Statistiques utilisateurs')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("Permet aux utilisateurs de consulter leurs statistiques personnelles (documents modifiés, rubriques générées, mots générés).")}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={statisticsEnabled}
                        onChange={handleSwitchChange}
                        disabled={submitting}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {statisticsEnabled ? t('Statistiques activées') : t('Statistiques désactivées')}
                      </Typography>
                    }
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 6 }}>
                    {statisticsEnabled 
                      ? t("Les utilisateurs peuvent consulter leurs statistiques personnelles. Les données sont enregistrées de manière anonyme (aucun contenu de document n'est stocké). Désactiver les statistiques ne supprime pas les données antérieures.")
                      : t("La page statistiques affichera un message indiquant que les statistiques ne sont pas activées. Aucune donnée ne sera enregistrée.")
                    }
                  </Typography>
                </Box>
              </Paper>

              <Paper sx={{ p: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {t('Informations sur la confidentialité')}
                </Typography>
                <Typography variant="body2">
                  {t("Les statistiques n'enregistrent que des compteurs anonymes (nombre de documents modifiés, rubriques générées, mots générés) associés au matricule de l'utilisateur. Aucun contenu de document, titre ou information sensible n'est stocké. Chaque utilisateur ne peut voir que ses propres statistiques.")}
                </Typography>
              </Paper>

              {message && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  {message}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </Box>
      </Fade>
    </Container>
  );
};

export default StatisticsAdmin;
